import { useCallback, useEffect, useMemo, useRef } from 'react'
import type {
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'

import {
  buildScopeKey,
  buildStorageKey,
  listStorageKeys,
  readStorageItem,
  writeStorageItem,
} from '@/components/Grid/storage'

import type { ColumnStateConfig, GridScope } from '../types'

type ColumnStateEvent =
  | ColumnMovedEvent
  | ColumnPinnedEvent
  | ColumnResizedEvent
  | ColumnVisibleEvent
  | SortChangedEvent

export const defaultIgnoreColumn = (colId: string) =>
  // AG Grid can materialize transient/internal columns while tree grouping,
  // selection, or tool panels are being reconfigured. Persisting them makes a
  // saved layout unstable across mounts because those ids are not part of the
  // feature-owned column contract.
  colId.startsWith('ag-Grid-')

// Persist only user-driven column changes (ignore autosize/api/init).
// Source list comes from AG Grid v33 `ColumnEventType` union.
export const USER_COLUMN_STATE_EVENT_SOURCES = new Set<string>([
  'uiColumnMoved',
  'uiColumnResized',
  'uiColumnDragged',
  'uiColumnSorted',
  'toolPanelUi',
  'toolPanelDragAndDrop',
  'columnMenu',
  'contextMenu',
])

export const isUserColumnStateEventSource = (source: unknown): boolean =>
  typeof source === 'string' && USER_COLUMN_STATE_EVENT_SOURCES.has(source)

type UseColumnStatePersistenceParams = {
  config?: ColumnStateConfig
  scope: GridScope
  api: GridApi | null
  gridIsReady: boolean
  columnDefsReady: boolean
}

export const parseColumnStatePayload = (
  raw: string | null,
): { columns: ColumnState[] } | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { columns?: unknown }
    return {
      columns: Array.isArray(parsed?.columns) ? parsed.columns : [],
    }
  } catch {
    return null
  }
}

const parseVersionParts = (version: string): number[] | null => {
  const parts = version.split('.')
  if (parts.length === 0) return null

  const parsed = parts.map((part) => Number(part))
  return parsed.every((part) => Number.isInteger(part) && part >= 0)
    ? parsed
    : null
}

const compareVersionParts = (left: number[], right: number[]) => {
  const maxLength = Math.max(left.length, right.length)
  for (let index = 0; index < maxLength; index += 1) {
    const leftPart = left[index] ?? 0
    const rightPart = right[index] ?? 0
    if (leftPart === rightPart) continue
    return leftPart - rightPart
  }
  return 0
}

export const findLatestCompatibleColumnStateKey = ({
  currentVersion,
  storageKeys,
  storagePrefix,
}: {
  currentVersion: string
  storageKeys: string[]
  storagePrefix: string
}): string | null => {
  const currentVersionParts = parseVersionParts(currentVersion)
  if (!currentVersionParts || currentVersionParts.length === 0) return null

  const currentMajor = currentVersionParts[0]
  const compatibleEntries = storageKeys
    .filter((key) => key.startsWith(storagePrefix))
    .map((key) => {
      const version = key.slice(storagePrefix.length)
      return {
        key,
        versionParts: parseVersionParts(version),
      }
    })
    .filter(
      (
        entry,
      ): entry is {
        key: string
        versionParts: number[]
      } =>
        Array.isArray(entry.versionParts) &&
        entry.versionParts.length > 0 &&
        entry.versionParts[0] === currentMajor &&
        compareVersionParts(entry.versionParts, currentVersionParts) < 0,
    )
    .sort((left, right) =>
      compareVersionParts(right.versionParts, left.versionParts),
    )

  return compatibleEntries[0]?.key ?? null
}

export const migrateLegacyColumnStatePayload = (params: {
  legacyRaw: string | null
  existingColIds: ReadonlySet<string>
  shouldIgnoreColumn: (colId: string) => boolean
  version: string
}): string | null => {
  const { legacyRaw, existingColIds, shouldIgnoreColumn, version } = params
  const legacyParsed = parseColumnStatePayload(legacyRaw)
  const legacyColumns = legacyParsed?.columns ?? []
  if (legacyColumns.length === 0) return null

  const legacyById = new Map(
    legacyColumns
      .filter(
        (col) =>
          typeof col.colId === 'string' && !shouldIgnoreColumn(col.colId),
      )
      .map((col) => [col.colId as string, col]),
  )

  const migrated: ColumnState[] = []
  legacyColumns.forEach((col) => {
    if (typeof col.colId !== 'string') return
    if (shouldIgnoreColumn(col.colId)) return
    if (!existingColIds.has(col.colId)) return
    migrated.push({
      ...col,
      hide: typeof col.hide === 'boolean' ? col.hide : false,
    })
  })

  existingColIds.forEach((colId) => {
    if (shouldIgnoreColumn(colId)) return
    if (legacyById.has(colId)) return
    migrated.push({ colId, hide: true })
  })

  return JSON.stringify({ version, columns: migrated })
}

export const migrateCompatibleColumnStatePayload = (params: {
  raw: string | null
  existingColIds: readonly string[]
  shouldIgnoreColumn: (colId: string) => boolean
  version: string
}): string | null => {
  const { raw, existingColIds, shouldIgnoreColumn, version } = params
  const parsed = parseColumnStatePayload(raw)
  const sourceColumns = parsed?.columns ?? []
  if (sourceColumns.length === 0) return null

  const sourceById = new Map(
    sourceColumns
      .filter(
        (col) =>
          typeof col.colId === 'string' && !shouldIgnoreColumn(col.colId),
      )
      .map((col) => [col.colId as string, col]),
  )

  let hasMigratedColumn = false
  const migrated = existingColIds.flatMap((colId) => {
    if (shouldIgnoreColumn(colId)) return []

    const sourceColumn = sourceById.get(colId)
    if (!sourceColumn) {
      return [{ colId }]
    }

    hasMigratedColumn = true
    const migratedColumn: ColumnState = { colId }
    if (typeof sourceColumn.hide === 'boolean') {
      migratedColumn.hide = sourceColumn.hide
    }
    if (typeof sourceColumn.width === 'number') {
      migratedColumn.width = sourceColumn.width
    }
    if (typeof sourceColumn.flex === 'number') {
      migratedColumn.flex = sourceColumn.flex
    }

    return [migratedColumn]
  })

  if (!hasMigratedColumn || migrated.length === 0) return null

  return JSON.stringify({ version, columns: migrated })
}

export const loadStoredColumnStatePayload = (params: {
  config?: ColumnStateConfig
  scope: GridScope
  existingColumnIds: readonly string[]
}): ColumnState[] => {
  const { config, scope, existingColumnIds } = params
  if (!config) return []

  const columnStatePersist = config.persist !== false
  if (!columnStatePersist) return []

  const shouldIgnoreColumn = config.ignoreColumn ?? defaultIgnoreColumn
  const scopeKey = buildScopeKey(scope)
  const columnStateStorageKey = config.storageKey
    ? config.storageKey(scope)
    : buildStorageKey('grid-columns', scopeKey, `v${config.version}`)
  const legacyColumnStateKey = config.legacyStorageKey
    ? config.legacyStorageKey(scope)
    : ''
  const compatibleColumnStateStoragePrefix = config.storageKey
    ? ''
    : `${buildStorageKey('grid-columns', scopeKey)}-v`

  const existing = new Set(existingColumnIds)

  let stored = readStorageItem(columnStateStorageKey)
  if (
    !stored &&
    config.migrateCompatibleVersions !== false &&
    compatibleColumnStateStoragePrefix
  ) {
    const compatibleStorageKey = findLatestCompatibleColumnStateKey({
      currentVersion: config.version,
      storageKeys: listStorageKeys(compatibleColumnStateStoragePrefix),
      storagePrefix: compatibleColumnStateStoragePrefix,
    })
    if (compatibleStorageKey) {
      const compatibleRaw = readStorageItem(compatibleStorageKey)
      const payload = migrateCompatibleColumnStatePayload({
        raw: compatibleRaw,
        existingColIds: existingColumnIds,
        shouldIgnoreColumn,
        version: config.version,
      })
      if (payload) stored = payload
    }
  }
  if (!stored && config.migrateLegacy !== false && legacyColumnStateKey) {
    const legacyRaw = readStorageItem(legacyColumnStateKey)
    const payload = migrateLegacyColumnStatePayload({
      legacyRaw,
      existingColIds: existing,
      shouldIgnoreColumn,
      version: config.version,
    })
    if (payload) stored = payload
  }

  if (!stored) return []

  const parsed = parseColumnStatePayload(stored)
  const storedColumns = parsed?.columns ?? []

  return storedColumns.filter(
    (col) =>
      typeof col.colId === 'string' &&
      existing.has(col.colId) &&
      !shouldIgnoreColumn(col.colId),
  )
}

const doesCurrentColumnStateMatchTarget = (params: {
  api: GridApi
  applyOrder: boolean
  columnIds: readonly string[]
  shouldIgnoreColumn: (colId: string) => boolean
  targetState: readonly ColumnState[]
}) => {
  const { api, applyOrder, columnIds, shouldIgnoreColumn, targetState } = params

  let currentState: ColumnState[] = []
  try {
    const nextState = api.getColumnState()
    currentState = Array.isArray(nextState) ? nextState : []
  } catch {
    return false
  }

  const existingColumnIds = new Set(columnIds)
  const filteredCurrentState = currentState.filter(
    (col) =>
      typeof col.colId === 'string' &&
      existingColumnIds.has(col.colId) &&
      !shouldIgnoreColumn(col.colId),
  )

  if (
    applyOrder &&
    (filteredCurrentState.length !== targetState.length ||
      filteredCurrentState.some(
        (col, index) => col.colId !== targetState[index]?.colId,
      ))
  ) {
    return false
  }

  const currentStateById = new Map(
    filteredCurrentState
      .filter(
        (col): col is ColumnState & { colId: string } =>
          typeof col.colId === 'string',
      )
      .map((col) => [col.colId, col]),
  )

  return targetState.every((targetColumn) => {
    if (typeof targetColumn.colId !== 'string') return false

    const currentColumn = currentStateById.get(targetColumn.colId)
    if (!currentColumn) return false

    return Object.entries(targetColumn).every(([key, value]) => {
      if (typeof value === 'undefined') return true
      return currentColumn[key as keyof ColumnState] === value
    })
  })
}

export const useColumnStatePersistence = ({
  config,
  scope,
  api,
  gridIsReady,
  columnDefsReady,
}: UseColumnStatePersistenceParams) => {
  const restoreRafRef = useRef<number | null>(null)
  const restoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const restoringRef = useRef(false)
  const lastSavedPayloadRef = useRef<string | null>(null)

  const columnStatePersist = config?.persist !== false
  const columnStateMigrateLegacy = config?.migrateLegacy !== false
  const columnStateMigrateCompatible =
    config?.migrateCompatibleVersions !== false

  const scopeKey = useMemo(() => {
    return buildScopeKey(scope)
  }, [scope])

  const columnStateStorageKey = useMemo(() => {
    if (!config) return ''
    if (config.storageKey) {
      return config.storageKey(scope)
    }
    return buildStorageKey('grid-columns', scopeKey, `v${config.version}`)
  }, [config, scope, scopeKey])

  const legacyColumnStateKey = useMemo(() => {
    return config?.legacyStorageKey ? config.legacyStorageKey(scope) : ''
  }, [config, scope])

  const compatibleColumnStateStoragePrefix = useMemo(() => {
    if (!config || config.storageKey) return ''
    return `${buildStorageKey('grid-columns', scopeKey)}-v`
  }, [config, scopeKey])

  const shouldIgnoreColumn = useCallback(
    (colId: string) =>
      config?.ignoreColumn
        ? config.ignoreColumn(colId)
        : defaultIgnoreColumn(colId),
    [config],
  )

  useEffect(() => {
    lastSavedPayloadRef.current = null
  }, [columnStateStorageKey])

  useEffect(() => {
    if (!config || !columnStatePersist) return
    if (!gridIsReady || !columnDefsReady) return
    if (!columnStateStorageKey) return

    if (!api) return

    const cancelScheduledRestore = () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.cancelAnimationFrame === 'function' &&
        restoreRafRef.current !== null
      ) {
        window.cancelAnimationFrame(restoreRafRef.current)
      }
      restoreRafRef.current = null

      if (restoreTimeoutRef.current !== null) {
        clearTimeout(restoreTimeoutRef.current)
      }
      restoreTimeoutRef.current = null
    }

    const apply = (trigger: 'initial' | 'newColumnsLoaded') => {
      if (api.isDestroyed?.()) return

      const existingColumnIds =
        api.getColumns()?.map((column) => column.getId()) ?? []
      if (existingColumnIds.length === 0) return

      const nextState = loadStoredColumnStatePayload({
        config: {
          ...config,
          migrateCompatibleVersions: columnStateMigrateCompatible,
          migrateLegacy: columnStateMigrateLegacy,
        },
        scope,
        existingColumnIds,
      })

      if (nextState.length === 0) return

      const applyOrder = config.applyOrder !== false

      if (
        trigger === 'newColumnsLoaded' &&
        doesCurrentColumnStateMatchTarget({
          api,
          applyOrder,
          columnIds: existingColumnIds,
          shouldIgnoreColumn,
          targetState: nextState,
        })
      ) {
        return
      }

      try {
        restoringRef.current = true
        api.applyColumnState({
          state: nextState,
          applyOrder,
        })
      } finally {
        restoringRef.current = false
      }
    }

    const scheduleApply = (trigger: 'initial' | 'newColumnsLoaded') => {
      // Applying column state can briefly put AG Grid internals in an in-between state
      // (observed: header menu asking for getColDef on a null column). Deferring by a tick
      // makes the operation more stable across mounts/re-mounts and dynamic colDefs.
      cancelScheduledRestore()

      if (
        typeof window !== 'undefined' &&
        typeof window.requestAnimationFrame === 'function'
      ) {
        restoreRafRef.current = window.requestAnimationFrame(() => {
          restoreRafRef.current = null
          if (restoringRef.current) return
          apply(trigger)
        })
      } else {
        restoreTimeoutRef.current = setTimeout(() => {
          restoreTimeoutRef.current = null
          if (restoringRef.current) return
          apply(trigger)
        }, 0)
      }
    }

    const handleNewColumnsLoaded = () => {
      scheduleApply('newColumnsLoaded')
    }

    scheduleApply('initial')
    api.addEventListener('newColumnsLoaded', handleNewColumnsLoaded)

    return () => {
      cancelScheduledRestore()
      if (api.isDestroyed?.()) return
      try {
        api.removeEventListener('newColumnsLoaded', handleNewColumnsLoaded)
      } catch {
        // ignore
      }
    }
  }, [
    config,
    compatibleColumnStateStoragePrefix,
    columnStateMigrateCompatible,
    columnStateMigrateLegacy,
    columnStatePersist,
    columnStateStorageKey,
    gridIsReady,
    api,
    columnDefsReady,
    legacyColumnStateKey,
    shouldIgnoreColumn,
  ])

  useEffect(() => {
    if (!config || !columnStatePersist) return
    if (!gridIsReady || !columnStateStorageKey) return

    if (!api) return

    const getSafeColumnState = (): ColumnState[] => {
      try {
        const state = api.getColumnState()
        return Array.isArray(state) ? state : []
      } catch {
        return []
      }
    }

    const saveColumnState = (event: ColumnStateEvent) => {
      if (!isUserColumnStateEventSource(event.source)) return
      if ('finished' in event && event.finished === false) return
      if (api.isDestroyed?.()) return

      const state = getSafeColumnState().filter(
        (col) =>
          typeof col.colId === 'string' && !shouldIgnoreColumn(col.colId),
      )
      if (state.length === 0) return

      const payload = JSON.stringify({
        version: config.version,
        columns: state,
      })
      if (payload === lastSavedPayloadRef.current) return

      const didWrite = writeStorageItem(columnStateStorageKey, payload)
      if (didWrite) {
        lastSavedPayloadRef.current = payload
      }
    }

    const events: Array<
      | 'columnVisible'
      | 'columnMoved'
      | 'columnResized'
      | 'columnPinned'
      | 'sortChanged'
    > = [
      'columnVisible',
      'columnMoved',
      'columnResized',
      'columnPinned',
      'sortChanged',
    ]

    events.forEach((eventName) => {
      api.addEventListener(eventName, saveColumnState)
    })

    return () => {
      if (api.isDestroyed?.()) return
      events.forEach((eventName) => {
        try {
          api.removeEventListener(eventName, saveColumnState)
        } catch {
          // ignore
        }
      })
    }
  }, [
    config,
    columnStatePersist,
    columnStateStorageKey,
    gridIsReady,
    shouldIgnoreColumn,
    api,
  ])
}
